import { trackLandingAccountCreated } from './landingExperimentAnalytics';

/**
 * Meta Pixel events fired from the app. The ad account optimizes on `Lead`,
 * with `StartLead` as the higher-volume mid-funnel signal it learns from
 * while `Lead` is still scarce. Server-side copies of the signup events are
 * sent by the API under the same ids (see sendFacebookConversion), so the
 * pair is counted once.
 */

type Fbq = (...args: unknown[]) => void;

function fbq(): Fbq | undefined {
  if (typeof window === 'undefined') return undefined;
  const fn = (window as unknown as { fbq?: Fbq }).fbq;
  return typeof fn === 'function' ? fn : undefined;
}

/** Someone began the signup form. Fires once per page load. */
let startLeadFired = false;
export function trackStartLead(source: string): void {
  if (startLeadFired) return;
  const track = fbq();
  if (!track) return;
  startLeadFired = true;
  track('trackCustom', 'StartLead', { content_category: source });
}

/**
 * A listing opened on /browse, as Meta's standard ViewContent so the ad
 * account can optimize toward people who look at homes and, later, build a
 * catalog audience from `content_ids`. Fires once per listing per page load.
 */
const viewedListingIds = new Set<string>();
export function trackListingViewContent(listing: {
  mlsListingId: string;
  price?: number;
  city?: string;
  state?: string;
}): void {
  if (viewedListingIds.has(listing.mlsListingId)) return;
  const track = fbq();
  if (!track) return;
  viewedListingIds.add(listing.mlsListingId);
  track('track', 'ViewContent', {
    content_ids: [listing.mlsListingId],
    content_type: 'home_listing',
    content_category: 'browse',
    value: listing.price,
    currency: 'USD',
    city: listing.city,
    region: listing.state,
  });
}

/** A completed signup, reported as a registration and as a lead. */
export function trackSignup(userId: string): void {
  trackLandingAccountCreated(userId);
  const track = fbq();
  if (!track) return;
  track('track', 'CompleteRegistration', { status: 'registered' }, { eventID: `reg_${userId}` });
  track('track', 'Lead', { content_name: 'account_signup', lead_kind: 'account_signup' }, { eventID: `lead_${userId}` });
}
